import React, { FC, useContext } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import pathfinder from '@fiverr-private/pathfinder';
import { Heading, Link } from '@fiverr-private/typography';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { ArrowTopRightIcon } from '@fiverr-private/visuals';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { EXPERT_BANNER_TYPE, tracker } from '@fiverr-private/listing_lib';
import { AppContext } from '../../../context/listingsContext';
import { BI_EVENT_TYPES, mixPanelBasePayload, T_PREFIX } from '../util';
import { getImpressionEventPayload, reportBigQueryBannerClick } from '../bqReports';
import {
    AI_CONSULTATION_BANNER_BG_URL,
    AI_CONSULTATION_BANNER_BG_WOMAN_URL,
    AI_CONSULTATION_BANNER_BG_CHAT_URL,
} from './constants';

interface AIConsultationBannerProps {
    resultsCount: number;
    hasActiveFilters: boolean;
    position?: number;
    userData?: {
        role: string;
    };
}

export const AIConsultationBanner: FC<AIConsultationBannerProps> = ({
    resultsCount,
    hasActiveFilters,
    position,
    userData,
}) => {
    const { isExpertListings } = useContext(AppContext);

    const [mainContainerRef] = useImpressionItem({
        enrichment: getImpressionEventPayload(
            isExpertListings,
            resultsCount,
            hasActiveFilters,
            position,
            EXPERT_BANNER_TYPE.AI_CONSULTATION
        ),
        onImpression: () => {
            tracker.trackBannerImpression(
                EXPERT_BANNER_TYPE.AI_CONSULTATION,
                mixPanelBasePayload(userData?.role, isExpertListings)
            );
        },
    });

    const openAIConsultationPage = () => {
        reportBigQueryBannerClick(
            BI_EVENT_TYPES.CLICK_ON_AI_SERVICE_BANNER,
            isExpertListings,
            resultsCount,
            hasActiveFilters,
            position
        );

        tracker.trackAIConsultationBannerClick(mixPanelBasePayload(userData?.role, isExpertListings));
    };

    return (
        <Link
            href={pathfinder(
                'content_pages_page',
                { page_slug: 'ai-dev-services' },
                { query: { source: 'fip_ai_listings' } }
            )}
            onClick={openAIConsultationPage}
            decoration="none"
        >
            <Stack
                ref={mainContainerRef}
                borderRadius="xl"
                justifyContent="spaceBetween"
                alignItems={{ default: 'center', sm: 'stretch' }}
                gap={{ default: '0', md: '4' }}
                style={{
                    backgroundPosition: 'left',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundImage: `url(${AI_CONSULTATION_BANNER_BG_URL})`,
                }}
            >
                <Stack
                    padding="8"
                    direction="column"
                    gap="5"
                    paddingRight={{ default: '8', sm: '0' }}
                    alignItems="flexStart"
                >
                    <Heading
                        color="white"
                        fontWeight="bold"
                        maxWidth={{
                            default: '100%',
                            lg: '514px',
                        }}
                        size={{
                            default: 'h_md',
                            md: 'h_lg',
                        }}
                    >
                        <I18n k={`${T_PREFIX}.ai_consultation.title`} />
                    </Heading>
                    <Button colorScheme="white" size="sm" variant="filled">
                        <I18n k={`${T_PREFIX}.ai_consultation.button`} />
                        <ArrowTopRightIcon />
                    </Button>
                </Stack>
                <Container
                    alignContent="center"
                    display={{ default: 'none', sm: 'block', md: 'none' }}
                    flexShrink={0}
                    style={{
                        backgroundImage: `url(${AI_CONSULTATION_BANNER_BG_CHAT_URL})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center 90%',
                        backgroundRepeat: 'no-repeat',
                    }}
                    width="300px"
                />
                <Container
                    minHeight="206px"
                    display={{ default: 'none', md: 'block' }}
                    style={{
                        backgroundImage: `url(${AI_CONSULTATION_BANNER_BG_WOMAN_URL})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'top',
                    }}
                    flexShrink={0}
                    width="416px"
                />
            </Stack>
        </Link>
    );
};
